// src/features/settings/components/non-working-days-panel/NonWorkingDaysPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Alert,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  AutoAwesome as GenerateIcon,
} from '@mui/icons-material';
import { useNonWorkingDays } from '../../hooks/useNonWorkingDays';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import { useNonWorkingDayPermissions } from '../../../auth/hooks/usePermissions';
import NonWorkingDaysGrid from './NonWorkingDaysGrid';
import NonWorkingDayDialog from './NonWorkingDayDialog';

export default function NonWorkingDaysPanel() {
  const { t } = useTranslation(['settings', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = useNonWorkingDayPermissions();

  const {
    nonWorkingDays,
    totalCount,
    loading,
    error,
    clearError,
    fetchNonWorkingDays,
    deleteNonWorkingDay,
    generateHolidays,
  } = useNonWorkingDays();

  const [kindFilter, setKindFilter] = useState('ALL');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedNonWorkingDay, setSelectedNonWorkingDay] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const canCreate = permissions.canCreate;
  const canUpdate = permissions.canUpdate;
  const canDelete = permissions.canDelete;

  const loadParams = useCallback(() => ({
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    kind: kindFilter === 'ALL' ? undefined : kindFilter,
  }), [paginationModel, kindFilter]);

  useEffect(() => {
    fetchNonWorkingDays(loadParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNonWorkingDays, paginationModel, kindFilter]);

  const handleKindFilterChange = useCallback((event, newKind) => {
    if (newKind !== null) {
      setKindFilter(newKind);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedNonWorkingDay(null);
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedNonWorkingDay(row);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback((row) => {
    showConfirmDialog({
      title: t('settings:non_working_days.confirm.delete_title'),
      message: t('settings:non_working_days.confirm.delete_message', { name: row.name }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteNonWorkingDay(row.non_working_day_id);
        if (result.success) {
          showSuccess(t('settings:non_working_days.success.deleted'));
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, deleteNonWorkingDay, showSuccess, showError, t]);

  const handleDialogClose = useCallback((shouldRefresh) => {
    setDialogOpen(false);
    setSelectedNonWorkingDay(null);
    if (shouldRefresh) {
      fetchNonWorkingDays(loadParams());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNonWorkingDays, loadParams]);

  const handleRefresh = useCallback(() => {
    fetchNonWorkingDays(loadParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNonWorkingDays, loadParams]);

  const currentYear = new Date().getFullYear();
  const handleGenerate = useCallback(() => {
    showConfirmDialog({
      title: t('settings:non_working_days.confirm.generate_title'),
      message: t('settings:non_working_days.confirm.generate_message', { year: currentYear }),
      onConfirm: async () => {
        const result = await generateHolidays({
          start_date: `${currentYear}-01-01`,
          end_date: `${currentYear}-12-31`,
        });
        if (result.success) {
          showSuccess(t('settings:non_working_days.success.generated'));
          fetchNonWorkingDays(loadParams());
        } else {
          showError(result.error);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfirmDialog, generateHolidays, fetchNonWorkingDays, loadParams, currentYear, showSuccess, showError, t]);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('settings:non_working_days.description')}
        </Typography>

        <Box mb={2} display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup
            value={kindFilter}
            exclusive
            onChange={handleKindFilterChange}
            size="small"
          >
            <ToggleButton value="ALL">{t('settings:non_working_days.filter.all')}</ToggleButton>
            <ToggleButton value="PUBLIC_HOLIDAY">{t('settings:non_working_days.filter.public_holiday')}</ToggleButton>
            <ToggleButton value="CLOSING_DAY">{t('settings:non_working_days.filter.closing_day')}</ToggleButton>
          </ToggleButtonGroup>

          <Box flex={1} />

          {canCreate && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreate}
              >
                {t('settings:non_working_days.buttons.add')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<GenerateIcon />}
                onClick={handleGenerate}
                disabled={loading}
              >
                {t('settings:non_working_days.buttons.generate')}
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            {t('common:buttons.refresh')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <NonWorkingDaysGrid
          rows={nonWorkingDays}
          loading={loading}
          totalCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Paper>

      {dialogOpen && (
        <NonWorkingDayDialog
          open={dialogOpen}
          nonWorkingDay={selectedNonWorkingDay}
          onClose={handleDialogClose}
        />
      )}

      <ConfirmDialog />
    </Box>
  );
}
